import { axiosInstance } from "../../../services/v2/axiosInstance";

/**
 * Avisos al instante de WappChat y Equipo (servicio wapp-push del servidor, Esteban 2026-10-04: sin consultar cada 15 s).
 * Una sola conexión (EventSource) para toda la pestaña. Los avisos traen solo ids: quien escucha pide el detalle a la API.
 * Si no hay servicio o se cae, `conectado` queda en false y las pantallas siguen con una consulta lenta de respaldo.
 */
type Oyente = (data: any) => void;

const urlServicio = (): string | null => {
	if (process.env.REACT_APP_PUSH_URL) return process.env.REACT_APP_PUSH_URL.replace(/\/$/, "");
	try {
		return new URL(process.env.REACT_APP_API_URL_V2 || "").origin + "/push";
	} catch {
		return null;
	}
};

class Push {
	conectado = false;
	private es: EventSource | null = null;
	private oyentes: { [evento: string]: Set<Oyente> } = {};
	private usos = 0;
	private campusId: number | null = null;
	private reintento: any = null;
	private fallos = 0;
	/** Ya se está pidiendo el boleto: evita abrir dos conexiones (el navegador solo deja 6 por servidor). */
	private abriendo = false;

	/** Lo llama WappChat al abrir; devuelve con qué soltarlo al cerrar. */
	usar(campusId: number): () => void {
		this.usos++;
		this.campusId = campusId;
		if (!this.es) this.abrir();
		return () => {
			this.usos = Math.max(0, this.usos - 1);
			if (!this.usos) this.cerrar();
		};
	}

	on(evento: string, fn: Oyente): () => void {
		(this.oyentes[evento] = this.oyentes[evento] || new Set()).add(fn);
		return () => { this.oyentes[evento]?.delete(fn); };
	}

	private emitir(evento: string, data: any) {
		this.oyentes[evento]?.forEach((fn) => { try { fn(data); } catch { /* un oyente roto no tumba a los demás */ } });
	}

	private async abrir() {
		const base = urlServicio();
		if (!base || !this.campusId || typeof EventSource === "undefined" || this.abriendo || this.es) return;
		this.abriendo = true;
		try {
			// El boleto es de la persona, no de una sección: así también le llegan los avisos al instante a quien no tiene WappChat.
			const r = await axiosInstance.get("/avisos/_pushTicket");
			const ticket = r.data?.ticket;
			if (!ticket || !this.usos || this.es) { this.abriendo = false; return; }   // sin servicio en el servidor: se queda la consulta lenta
			const es = new EventSource(`${base}/stream?t=${encodeURIComponent(ticket)}`);
			this.es = es;
			this.abriendo = false;
			es.addEventListener("hola", () => { this.fallos = 0; this.ponerConectado(true); });
			["wa", "equipo", "equipo_visto", "equipo_escribe", "avisos_ia", "aviso", "correo", "llamada", "festejo", "conteos", "conteos_sup", "captura", "conteo"].forEach((ev) => es.addEventListener(ev, (e: MessageEvent) => {
				let d: any = {};
				try { d = JSON.parse(e.data); } catch { /* sin datos */ }
				this.emitir(ev, d);
			}));
			es.onerror = () => {
				this.ponerConectado(false);
				// El navegador reintenta solo; si el boleto venció (401) se cierra: se pide otro con espera creciente.
				if (es.readyState === EventSource.CLOSED) {
					this.es = null;
					this.fallos++;
					clearTimeout(this.reintento);
					if (this.usos) this.reintento = setTimeout(() => this.abrir(), Math.min(60000, 3000 * this.fallos));
				}
			};
		} catch {
			this.abriendo = false;
			this.fallos++;
			clearTimeout(this.reintento);
			if (this.usos) this.reintento = setTimeout(() => this.abrir(), Math.min(60000, 5000 * this.fallos));
		}
	}

	private ponerConectado(v: boolean) {
		if (this.conectado === v) return;
		this.conectado = v;
		this.emitir("estado", { conectado: v });
	}

	private cerrar() {
		clearTimeout(this.reintento);
		this.es?.close();
		this.es = null;
		this.ponerConectado(false);
	}
}

export const push = new Push();

/** Respaldo cuando no hay conexión: cada 15 s; con conexión, cada 2 min por si se perdió algo. */
export const intervaloRespaldo = () => (push.conectado ? 120000 : 15000);
